import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, ChevronDown, ChevronRight, Users, Building2, Crown, Download, FileText, FileSpreadsheet, LayoutGrid, List } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import OrgChartViewer from '../components/org-chart/OrgChartViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const OrgChart = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState('all');
  const [expandedMgrs, setExpandedMgrs] = useState({});
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'chart'

  // Check if current user is super_admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  const handleDownload = async (format) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/org-chart/download/${format}?department=${filterDept}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Organization_Chart.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Downloaded', description: `Org chart ${format.toUpperCase()} saved` });
    } catch {
      toast({ title: 'Error', description: 'Download failed', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/employees?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(res.data);
      const expanded = {};
      const leaderRoles = ['Owner', 'Top Management', 'Management', 'Manager'];
      res.data.filter(e => leaderRoles.includes(e.employee_role)).forEach(m => { expanded[m.id] = true; });
      setExpandedMgrs(expanded);
    } catch {
      toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleReassign = useCallback(async (employeeId, newManagerId, mode) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/api/org-chart/reassign`, {
        employee_id: employeeId,
        new_manager_id: newManagerId,
        mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: 'Reassigned',
        description: `${res.data.employee_name} now reports to ${res.data.new_manager_name}`,
      });
      // Refresh employees to reflect the change
      await fetchEmployees();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reassignment failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      throw err; // rethrow so the dialog knows it failed
    }
  }, [toast, fetchEmployees]);

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();

  // Build hierarchy
  const buildHierarchy = () => {
    const leaderRoles = ['Owner', 'Top Management', 'Management', 'Manager'];
    const filtered = filterDept === 'all' ? employees : employees.filter(e => e.department === filterDept);
    const leaders = filtered.filter(e => leaderRoles.includes(e.employee_role));
    const staffWithMgr = filtered.filter(e => !leaderRoles.includes(e.employee_role) && e.reporting_manager_ids?.length);
    const staffNoMgr = filtered.filter(e => !leaderRoles.includes(e.employee_role) && !e.reporting_manager_ids?.length);

    // Group staff under leaders
    const mgrMap = {};
    leaders.forEach(m => { mgrMap[m.id] = { manager: m, reports: [] }; });

    staffWithMgr.forEach(s => {
      let placed = false;
      for (const mid of (s.reporting_manager_ids || [])) {
        if (mgrMap[mid]) {
          mgrMap[mid].reports.push(s);
          placed = true;
          break;
        }
      }
      if (!placed) {
        for (const mid of (s.reporting_manager_ids || [])) {
          const mgr = employees.find(e => e.id === mid);
          if (mgr) {
            if (!mgrMap[mgr.id]) mgrMap[mgr.id] = { manager: mgr, reports: [] };
            mgrMap[mgr.id].reports.push(s);
            break;
          }
        }
      }
    });

    // Group by department
    const deptGroups = {};
    Object.values(mgrMap).forEach(({ manager, reports }) => {
      const dept = manager.department;
      if (!deptGroups[dept]) deptGroups[dept] = { managers: [], unassigned: [] };
      deptGroups[dept].managers.push({ manager, reports });
    });

    staffNoMgr.forEach(s => {
      const dept = s.department;
      if (!deptGroups[dept]) deptGroups[dept] = { managers: [], unassigned: [] };
      deptGroups[dept].unassigned.push(s);
    });

    return deptGroups;
  };

  const hierarchy = buildHierarchy();
  const toggleMgr = (id) => setExpandedMgrs(prev => ({ ...prev, [id]: !prev[id] }));

  // Stats
  const leaderRoles = ['Owner', 'Top Management', 'Management', 'Manager'];
  const totalManagers = employees.filter(e => leaderRoles.includes(e.employee_role)).length;
  const totalStaff = employees.filter(e => !leaderRoles.includes(e.employee_role)).length;
  const assignedStaff = employees.filter(e => !leaderRoles.includes(e.employee_role) && e.reporting_manager_ids?.length).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-chart-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organization Chart</h1>
          <p className="text-gray-500 mt-1">Manager → Staff reporting hierarchy</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              data-testid="view-list-btn"
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition ${viewMode === 'chart' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              data-testid="view-chart-btn"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Chart
            </button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={downloading} data-testid="org-download-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Downloading...' : 'Download'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-1" align="end">
              <button
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                onClick={() => handleDownload('pdf')}
                data-testid="download-pdf-btn"
              >
                <FileText className="h-4 w-4 text-red-500" /> Download PDF
              </button>
              <button
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                onClick={() => handleDownload('excel')}
                data-testid="download-excel-btn"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-600" /> Download Excel
              </button>
            </PopoverContent>
          </Popover>
          <div className="w-56">
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger data-testid="org-dept-filter">
                <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Crown className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalManagers}</p>
              <p className="text-xs text-gray-500">Managers</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalStaff}</p>
              <p className="text-xs text-gray-500">Staff</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{assignedStaff}/{totalStaff}</p>
              <p className="text-xs text-gray-500">Assigned to Manager</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hierarchy */}
      {viewMode === 'chart' ? (
        <OrgChartViewer
          hierarchy={hierarchy}
          employees={employees}
          isSuperAdmin={isSuperAdmin}
          onReassign={handleReassign}
        />
      ) : (
      <div className="space-y-6">
        {Object.keys(hierarchy).sort().map(dept => {
          const { managers, unassigned } = hierarchy[dept];
          const deptTotal = managers.reduce((a, m) => a + 1 + m.reports.length, 0) + unassigned.length;
          return (
            <div key={dept} className="border rounded-xl bg-white overflow-hidden" data-testid={`dept-${dept}`}>
              {/* Department Header */}
              <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <h2 className="font-semibold text-gray-800">{dept}</h2>
                </div>
                <Badge variant="secondary" className="text-xs">{deptTotal} people</Badge>
              </div>

              <div className="p-4 space-y-3">
                {managers.map(({ manager, reports }) => (
                  <div key={manager.id}>
                    {/* Manager Row */}
                    <button
                      onClick={() => toggleMgr(manager.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition group text-left"
                      data-testid={`mgr-node-${manager.id}`}
                    >
                      {reports.length > 0 ? (
                        expandedMgrs[manager.id] 
                          ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <div className="w-4 flex-shrink-0" />
                      )}
                      <PersonAvatar employee={manager} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{manager.name}</span>
                          <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0 h-4">Manager</Badge>
                          {manager.department !== dept && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{manager.department}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{manager.position}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs flex-shrink-0">
                        {reports.length} report{reports.length !== 1 ? 's' : ''}
                      </Badge>
                    </button>

                    {/* Reports */}
                    {expandedMgrs[manager.id] && reports.length > 0 && (
                      <div className="ml-7 pl-4 border-l-2 border-gray-200 space-y-1 mt-1">
                        {reports.map(staff => (
                          <div key={staff.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition"
                            data-testid={`staff-node-${staff.id}`}>
                            <div className="w-2 h-px bg-gray-300 flex-shrink-0" />
                            <PersonAvatar employee={staff} size="sm" />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-800">{staff.name}</span>
                              <p className="text-xs text-gray-400">{staff.position}</p>
                            </div>
                            {staff.department !== dept && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{staff.department}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Unassigned staff */}
                {unassigned.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed">
                    <p className="text-xs text-gray-400 mb-2 px-3">No reporting manager assigned</p>
                    <div className="space-y-1">
                      {unassigned.map(staff => (
                        <div key={staff.id} className="flex items-center gap-3 p-2 px-3 rounded-lg hover:bg-gray-50 transition">
                          <div className="w-4 flex-shrink-0" />
                          <PersonAvatar employee={staff} size="sm" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-600">{staff.name}</span>
                            <p className="text-xs text-gray-400">{staff.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {managers.length === 0 && unassigned.length === 0 && (
                  <p className="text-center py-4 text-sm text-gray-400">No employees in this department</p>
                )}
              </div>
            </div>
          );
        })}

        {Object.keys(hierarchy).length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-gray-400">
              No organization data available
            </CardContent>
          </Card>
        )}
      </div>
      )}
    </div>
  );
};

const PersonAvatar = ({ employee, size = 'sm' }) => {
  const sz = size === 'md' ? 'w-9 h-9' : 'w-7 h-7';
  const iconSz = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  return (
    <div className={`${sz} rounded-full border overflow-hidden flex items-center justify-center flex-shrink-0 bg-gray-100 border-gray-200`}>
      {employee.photo_url ? (
        <img src={`${API}${employee.photo_url}`} alt="" className="w-full h-full object-cover" />
      ) : (
        <User className={`${iconSz} text-gray-400`} />
      )}
    </div>
  );
};

export default OrgChart;
